import React, { useEffect, useState } from 'react';
import { When } from 'react-if';
import { useEffectOnce } from 'react-use';
import cns from 'classnames';
import { debounce } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useViewportDevice } from '@zep/react';
import { SpacesQuery } from '@zep/types';
import { Button, Input } from '@zep/ui';
import { cn } from '@zep/utils';
import { useSearchFilterContext } from '../../../../contexts';
import { useModalActions } from '../../../../hooks';
import { MobileSpaceSearchInput } from '../MobileSearchInput';
import { CreateSpaceButton } from './CreateSpaceButton';
import S from './TopBar.module.scss';
export const TopBar = ({
  spaceContentRef
}: {
  spaceContentRef: React.RefObject<HTMLDivElement>;
}) => {
  const {
    isShowSearchInput
  } = useSearchFilterContext();
  const {
    isPortableDevice,
    isMobile
  } = useViewportDevice();
  const [isSticky, setIsSticky] = useState(false);
  const [showPortableSearchInput, setShowPortableSearchInput] = useState(false);
  useEffectOnce(() => {
    // subLayout이 mainLayout의 바텀에 도달했는지에 따른 상태 변경
    const onSticky = () => {
      // @ts-ignore
      const sticky = spaceContentRef.current?.offsetTop - window.scrollY <= 64;
      setIsSticky(sticky);
      if (sticky) {
        document.body.classList.add('gnb_no_shadow');
      } else {
        document.body.classList.remove('gnb_no_shadow');
      }
    };
    window.addEventListener('scroll', onSticky);
    return () => {
      window.removeEventListener('scroll', onSticky);
    };
  });
  return <div className={cn(S.top_bar_layout, {
    [S.sticky]: isSticky
  })} data-sentry-component="TopBar" data-sentry-source-file="TopBar.tsx">
      <div className={S.top_bar_wrapper}>
        <div className={S.top_bar_inner}>
          <div className={S.top_bar_left}>
            <SpaceFilter data-sentry-element="SpaceFilter" data-sentry-source-file="TopBar.tsx" />
          </div>
          <div className={S.top_bar_right}>
            <When condition={isShowSearchInput} data-sentry-element="When" data-sentry-source-file="TopBar.tsx">
              {isPortableDevice ? <Button transparent onClick={() => setShowPortableSearchInput(prev => !prev)}>
                  <img src="/images/light/spaceMe/search_gray.png" alt="search" width={24} height={24} />
                </Button> : <SpaceSearchInput />}
            </When>
            <When condition={!isMobile} data-sentry-element="When" data-sentry-source-file="TopBar.tsx">
              <EnterCodeButton data-sentry-element="EnterCodeButton" data-sentry-source-file="TopBar.tsx" />
              <CreateSpaceButton data-sentry-element="CreateSpaceButton" data-sentry-source-file="TopBar.tsx" />
            </When>
          </div>
        </div>
        <When condition={isShowSearchInput && showPortableSearchInput && isPortableDevice} data-sentry-element="When" data-sentry-source-file="TopBar.tsx">
          <MobileSpaceSearchInput data-sentry-element="MobileSpaceSearchInput" data-sentry-source-file="TopBar.tsx" />
        </When>
      </div>
    </div>;
};
const SpaceFilter = () => {
  const {
    t
  } = useTranslation(['home']);
  const {
    filterType,
    setFilterType,
    setSearchQuery
  } = useSearchFilterContext();
  useEffect(() => {
    setSearchQuery('');
  }, [setSearchQuery, filterType]);
  return <>
      <span className={cns(S.tab_button, filterType === SpacesQuery.RECENT && S.active)} onClick={() => setFilterType(SpacesQuery.RECENT)}>
        {t('home:space_me_recently_visited')}
      </span>
      <img src="/images/light/spaceMe/divider_gray.png" alt="divider" width={1} height={14} style={{
      margin: '10px'
    }} />
      <span className={cns(S.tab_button, filterType === SpacesQuery.MY && S.active)} onClick={() => setFilterType(SpacesQuery.MY)}>
        {t('home:space_me_related_my_spaces')}
      </span>
    </>;
};
export const MAXIMUM_QUERY_LENGTH = 30;
const SpaceSearchInput = () => {
  const {
    t
  } = useTranslation(['home']);
  const {
    setSearchQuery,
    filterType
  } = useSearchFilterContext();
  const debouncedSetSearchQuery = React.useMemo(() => debounce(setSearchQuery, 600), [setSearchQuery]);
  const [searchValue, setSearchValue] = useState('');
  useEffect(() => {
    if (searchValue.length >= 2) {
      debouncedSetSearchQuery(searchValue);
    } else {
      debouncedSetSearchQuery('');
    }
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [searchValue, debouncedSetSearchQuery]);
  useEffect(() => {
    setSearchValue('');
  }, [filterType]);
  return <Input className={S.custom_input} value={searchValue} leftSlot={<img src="/images/light/spaceMe/search_gray.png" alt="search" width={20} height={20} />} placeholder={filterType === SpacesQuery.RECENT ? t('home:space_me_search_visited_spaces') : t('home:space_me_search_my_spaces')} onChange={e => setSearchValue(e.target.value)} maxLength={MAXIMUM_QUERY_LENGTH} data-sentry-element="Input" data-sentry-component="SpaceSearchInput" data-sentry-source-file="TopBar.tsx" />;
};
const EnterCodeButton = () => {
  const {
    t
  } = useTranslation(['home']);
  const {
    handleClickEnterWithCode
  } = useModalActions();
  return <Button leftSlot={<img src="/images/light/spaceMe/enter_space_primary.svg" alt="empty icon" />} size="md" variant="secondary" onClick={handleClickEnterWithCode} data-sentry-element="Button" data-sentry-component="EnterCodeButton" data-sentry-source-file="TopBar.tsx">
      {t('home:space_me_enter_with_code')}
    </Button>;
};